export interface CrossModeCelebrationDraft {
  title?: string;
  description?: string;
  celebration_type?: string;
  celebration_subtype?: string;
  template?: string;
  tags?: Record<string, string[]>;
  start_date?: string;
  end_date?: string;
  budget?: number;
  location_name?: string;
  latitude?: number;
  longitude?: number;
  radius_miles?: number;
  rsvp_deadline?: string;
  celebrant_names?: string[];
  celebrant_roles?: string[];
  celebrant_emails?: string[];
  is_surprise?: boolean;
  host_name?: string;
  event_date?: string;
  start_time?: string;
  end_time?: string;
  max_guests?: number;
  plus_ones_allowed?: boolean;
  plus_ones_limit?: number;
  guest_list_visible?: boolean;
  feature_profile?: {
    quick_mode_enabled: boolean;
    module_flags: Record<'venues' | 'services' | 'registry' | 'budget' | 'goals' | 'guest_wall', boolean>;
    service_mode?: 'order_food' | 'hybrid' | 'potluck';
  };
  updatedAt: string;
}

const CROSS_MODE_DRAFT_KEY = 'curvato.celebrationCrossModeDraft.v1';

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const toString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
};

const toBoolean = (value: unknown): boolean | undefined => {
  return typeof value === 'boolean' ? value : undefined;
};

const toStringArray = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const normalized = value
    .map((entry) => toString(entry))
    .filter((entry): entry is string => Boolean(entry));

  return normalized.length > 0 ? normalized : undefined;
};

const toTags = (value: unknown): Record<string, string[]> | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const entries = Object.entries(value)
    .map(([category, values]) => [category, toStringArray(values)] as const)
    .filter((entry): entry is readonly [string, string[]] => Boolean(entry[1]));

  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
};

const sanitizeDraft = (value: unknown): CrossModeCelebrationDraft | null => {
  if (!isRecord(value)) {
    return null;
  }

  const updatedAt = toString(value.updatedAt) || new Date().toISOString();

  return {
    title: toString(value.title),
    description: toString(value.description),
    celebration_type: toString(value.celebration_type),
    celebration_subtype: toString(value.celebration_subtype),
    template: toString(value.template),
    tags: toTags(value.tags),
    start_date: toString(value.start_date),
    end_date: toString(value.end_date),
    budget: toNumber(value.budget),
    location_name: toString(value.location_name),
    latitude: toNumber(value.latitude),
    longitude: toNumber(value.longitude),
    radius_miles: toNumber(value.radius_miles),
    rsvp_deadline: toString(value.rsvp_deadline),
    celebrant_names: toStringArray(value.celebrant_names),
    celebrant_roles: toStringArray(value.celebrant_roles),
    celebrant_emails: toStringArray(value.celebrant_emails),
    host_name: toString(value.host_name),
    event_date: toString(value.event_date),
    start_time: toString(value.start_time),
    end_time: toString(value.end_time),
    max_guests: toNumber(value.max_guests),
    plus_ones_allowed: toBoolean(value.plus_ones_allowed),
    plus_ones_limit: toNumber(value.plus_ones_limit),
    guest_list_visible: toBoolean(value.guest_list_visible),
    feature_profile: isRecord(value.feature_profile)
      ? {
        quick_mode_enabled: typeof value.feature_profile.quick_mode_enabled === 'boolean' ? value.feature_profile.quick_mode_enabled : true,
        module_flags: isRecord(value.feature_profile.module_flags)
          ? {
            venues: typeof value.feature_profile.module_flags.venues === 'boolean' ? value.feature_profile.module_flags.venues : false,
            services: typeof value.feature_profile.module_flags.services === 'boolean' ? value.feature_profile.module_flags.services : true,
            registry: typeof value.feature_profile.module_flags.registry === 'boolean' ? value.feature_profile.module_flags.registry : false,
            budget: typeof value.feature_profile.module_flags.budget === 'boolean' ? value.feature_profile.module_flags.budget : true,
            goals: typeof value.feature_profile.module_flags.goals === 'boolean' ? value.feature_profile.module_flags.goals : true,
            guest_wall: typeof value.feature_profile.module_flags.guest_wall === 'boolean' ? value.feature_profile.module_flags.guest_wall : false,
          }
          : {
            venues: false,
            services: true,
            registry: false,
            budget: true,
            goals: true,
            guest_wall: false,
          },
        service_mode: value.feature_profile.service_mode === 'hybrid' || value.feature_profile.service_mode === 'potluck'
          ? value.feature_profile.service_mode
          : 'order_food',
      }
      : undefined,
    updatedAt,
  };
};

export const readCrossModeDraft = (): CrossModeCelebrationDraft | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(CROSS_MODE_DRAFT_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as unknown;
    return sanitizeDraft(parsed);
  } catch {
    return null;
  }
};

export const writeCrossModeDraft = (draft: Partial<CrossModeCelebrationDraft>): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const previous = readCrossModeDraft();
    const sanitizedPatchEntries = Object.entries(draft).filter(([, value]) => value !== undefined);
    const sanitizedPatch = Object.fromEntries(sanitizedPatchEntries) as Partial<CrossModeCelebrationDraft>;
    const merged = {
      ...previous,
      ...sanitizedPatch,
      updatedAt: new Date().toISOString(),
    };

    window.localStorage.setItem(CROSS_MODE_DRAFT_KEY, JSON.stringify(merged));
  } catch {
    // Non-blocking: cross-mode continuity should never break user flow.
  }
};

export const clearCrossModeDraft = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.removeItem(CROSS_MODE_DRAFT_KEY);
  } catch {
    // Ignore storage cleanup errors.
  }
};
